/* Layout enhancements only: Shopify/Minimog owns variants, cart and payment. */
if (!customElements.get('mb-product-layout')) {
  customElements.define('mb-product-layout', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.init());
    }

    init() {
      this.productInfo = this.closest('product-info');
      this.panel = this.querySelector('.m-product-info--wrapper');
      this.form = this.querySelector('.m-product-form--main');
      this.bar = this.querySelector('[data-mb-mobile-bar]');
      this.header = document.querySelector('m-header');
      if (!this.productInfo || !this.panel) return;

      this.abort = new AbortController();
      const options = { signal: this.abort.signal, passive: true };
      this.schedule = () => {
        if (this.updateFrame) return;
        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.updateLayout();
        });
      };
      window.addEventListener('resize', this.schedule, options);
      window.addEventListener('scroll', this.schedule, options);
      document.addEventListener('variant:changed', this.schedule, options);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this.panel);
        if (this.header) this.resizeObserver.observe(this.header);
      }

      const price = this.querySelector('[id^="Price-"]');
      if (price && this.bar) {
        this.priceObserver = new MutationObserver(() => this.syncPrice(price));
        this.priceObserver.observe(price, { childList: true, subtree: true, characterData: true });
        this.syncPrice(price);
      }

      this.querySelector('[data-mb-jump-to-buy]')?.addEventListener('click', event => {
        if (!this.form) return;
        event.preventDefault();
        const warning = this.form.parentElement.querySelector('.mb-product-warning');
        const target = warning || this.form;
        const top = target.getBoundingClientRect().top + window.scrollY - this.headerOffset() - 18;
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        const button = this.form.querySelector('[name="add"]');
        if (button && !button.disabled) button.focus({ preventScroll: true });
      }, { signal: this.abort.signal });

      // Preserve plain contact details even if another app auto-links text later.
      this.contactObservers = [];
      this.querySelectorAll('[data-mb-contact-text]').forEach(contact => {
        const unlink = () => contact.querySelectorAll('a').forEach(link => link.replaceWith(...link.childNodes));
        unlink();
        const observer = new MutationObserver(unlink);
        observer.observe(contact, { childList: true, subtree: true });
        this.contactObservers.push(observer);
      });
      this.updateLayout();
    }

    headerOffset() {
      if (!this.header || this.header.dataset.sticky === 'none') return 12;
      const headerBox = this.header.querySelector('.m-header__wrapper')?.getBoundingClientRect();
      if (!headerBox || headerBox.bottom <= 0) return 12;
      return Math.max(12, Math.min(headerBox.height, headerBox.bottom));
    }

    updateLayout() {
      const offset = this.headerOffset();
      this.productInfo.style.setProperty('--mb-header-offset', `${offset}px`);
      // Tall purchase panels scroll normally until all controls can be reached.
      const stickyTop = Math.min(offset + 18, window.innerHeight - this.panel.offsetHeight - 18);
      this.productInfo.style.setProperty('--mb-sticky-top', `${stickyTop}px`);
      if (this.bar && this.form) {
        const pastForm = this.form.getBoundingClientRect().bottom < offset;
        this.bar.hidden = !(window.innerWidth < 768 && pastForm);
      }
    }

    syncPrice(price) {
      const source = price.querySelector('.m-price');
      const target = this.bar?.querySelector('.mb-mobile-purchase__price');
      if (!source || !target) return;
      const copy = source.cloneNode(true);
      copy.querySelectorAll('.m-price__badges').forEach(node => node.remove());
      copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      target.replaceChildren(copy);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.updateFrame);
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.contactObservers?.forEach(observer => observer.disconnect());
    }
  });
}
